import { proxyContainer } from './polyfill';
import {
    BrowserStorageStoreItem,
    BrowserStorageStoreSettings,
    Keys,
    ObserverCallback,
    Observers,
    Store,
    StoreSettings,
} from './store.types';

export function createStore<State extends object, Actions extends string>(
    settings: StoreSettings<State, Actions>,
): Store<State, Actions> {
    const observers: Observers<State> = [];

    let prevState: State = settings.initialState;

    const validator = {
        set(state: State, key: keyof State, value: any) {
            if (valueHasChanged(state[key], value)) {
                state[key] = value;
                callObservers(state, key);
            }

            return true;
        },
    };

    let state = proxyContainer<State>(settings.initialState || {}, validator);

    function subscribe(observer: ObserverCallback<State>, keys?: Keys<State>) {
        if (typeof observer !== 'function')
            new Error('You can only subscribe to Store changes with a valid function!');
        observers.push({ callback: observer, keys });
        return true;
    }

    function unsubscribe(observer: ObserverCallback<State>) {
        if (typeof observer !== 'function')
            new Error('You can only subscribe to Store changes with a valid function!');

        const match = observers.find(({ callback }) => callback === observer);
        if (match) {
            observers.splice(observers.indexOf(match), 1);
        }
    }

    async function dispatch(actionKey: Actions, payload: any) {
        if (!settings.actions || typeof settings.actions[actionKey] !== 'function')
            new Error(`Action "${actionKey}" doesn't exist.`);

        const action = settings.actions![actionKey];
        prevState = Object.assign({}, state);
        const newState = await action(state, payload);
        state = newState;

        return state;
    }

    function callObservers(data: State, key: any) {
        observers.forEach(({ keys, callback }) => {
            if (!keys) {
                callback(data);
            } else if (Array.isArray(keys) && keys.indexOf(key) > -1) {
                callback(data);
            }
        });
    }

    return {
        subscribe,
        unsubscribe,
        dispatch,
        getState: () => state,
        getPrevState: () => prevState,
    };
}

function valueHasChanged(value: unknown, old: unknown): boolean {
    // This ensures (old==NaN, value==NaN) always returns false
    return old !== value && (old === old || value === value);
}

export const createLocalStorageStore = <State extends object, Action extends string>({
    key,
    actions,
    initialState,
    expireInDays,
    excludeProps,
    initAction,
}: BrowserStorageStoreSettings<State, Action>) => {
    const store = createStore<State, Action>({
        initialState,
        actions,
    });

    function updateStore(data: State) {
        return store.dispatch(initAction, data);
    }

    function get(): State | undefined {
        const state = localStorage.getItem(key);
        if (!state) return;

        const parsedState = JSON.parse(state) as BrowserStorageStoreItem<State>;
        if (parsedState.expire && new Date() > parsedState.expire) {
            localStorage.removeItem(key);
            return;
        }

        return parsedState.value;
    }

    function set(state: State) {
        try {
            const now = new Date();
            const serializedState = JSON.stringify({
                value: excludeProps ? excludePropsFromState(state) : state,
                expire: expireInDays ? now.setDate(now.getDate() + expireInDays) : undefined,
            });

            localStorage.setItem(key, serializedState);
        } catch (error) {
            console.log('Error in localStorage.setItem', error);
        }
    }

    function excludePropsFromState(state: State) {
        const keys = Object.keys(state) as Keys<State>;
        const filteredState: State = {} as State;

        keys.filter((key) => !excludeProps?.includes(key)).forEach((key) => {
            const value = state[key];
            filteredState[key] = value;
        });

        return filteredState;
    }

    store.subscribe((data) => set(data));

    const data = get();

    if (data) updateStore(data);

    return { get, set, ...store };
};

export const createSessionStorageStore = <State extends object, Action extends string>({
    key,
    initialState,
    actions,
    initAction,
}: BrowserStorageStoreSettings<State, Action>) => {
    const store = createStore<State, Action>({
        initialState,
        actions,
    });

    function updateStore(data: State) {
        return store.dispatch(initAction, data);
    }

    function get(): State | undefined {
        const value = sessionStorage.getItem(key);
        if (!value) return;

        return JSON.parse(value);
    }

    function set(value: State) {
        try {
            const serializedState = JSON.stringify(value);

            sessionStorage.setItem(key, serializedState);
        } catch (error) {
            console.log('Error in sessionStorage.setItem', error);
        }
    }

    store.subscribe((data) => set(data));

    const data = get();
    if (data) updateStore(data);

    return { get, set, ...store };
};
